import {
  ActivityIndicator,
  Animated,
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
  StyleProp,
  ViewStyle,
} from 'react-native';
import {
  ItemDetailsAction,
  useBuyerManagement,
  useCatalogItemManagement,
  useGetRunForItemDetail,
  useItemDetailsContext,
  useLocationUpdate,
  useUPCOrder,
} from '../../../hooks/ItemDetails';
import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {getDefaultImageUrl, isWeb} from '../../../../utils/common';
import {
  useColorThemeType,
  useFontTheme,
  useShadow,
} from '@buncha/appStyles/hooks/theme';

import AdjacentOOSBanner from '../AdjacentOOSBanner';
import AdjacentOOSChat from '../AdjacentOOSChat';
import {BottomButton} from '@buncha/components/composites/Button';
import {Box} from '../../../../components/core/view';
import BuyerDetails from '../BuyerDetails/BuyerDetails';
import Carousal from '../../../../components/composites/Carousal/Carousal';
import ChangeCategory from '../../changeCategory';
import {Divider} from 'native-base';
import {H6Text} from '@buncha/components/core/text';
import If from '../../../../components/conditional/If';
import ItemInfo from '../ItemInfo/ItemInfo';
import ItemLocationInfo from '../ItemLocationInfo';
import {ItemLocationInformation} from '@buncha/itemDetails/types/itemDetails';
import ItemQuantityPill from '../ItemQuantityPill/index';
import LoaderComponents from '@buncha/components/composites/CustomLoader/LoaderComponents';
import {Modules} from '@buncha/utils/types';
import QRCode from '@buncha/components/core/icon/QRCode';
import {ShopperScreens} from '@buncha/home/navigation/types';
import {styles} from './styles';
import {useAppSelector} from '@buncha/config/redux/hooks';
import {useGetLocale} from '@buncha/localization/hooks';
import {useIsMeijerStore} from '@buncha/home/hooks/home';
import {useRoute} from '@react-navigation/native';

interface RouteParamsType {
  storeIndex?: string;
  catalogItemId?: number;
  orderItemId?: number;
  runId?: number;
  storeId?: number;
}

interface Props {
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  setLoading: (loading: boolean) => void;
  isUPCMode?: boolean;
  loading?: boolean;
  viewReplacementBannerStyle?: StyleProp<ViewStyle>;
  bottomButtonLoading?: boolean;
  onButtonPress: (sMasterId?: number) => void;
  buttonText: string;
  hasPriceTag?: boolean;
}
const ItemDetailsBody = (props: Props) => {
  const {state, dispatch} = useItemDetailsContext();
  const route = useRoute();
  const [getRunForItemDetail, runState] = useGetRunForItemDetail();
  const {getCatalogItemForShopper, loadingItem} =
    useCatalogItemManagement(dispatch);
  const {getBuyersForItem, loadingBuyer} = useBuyerManagement(dispatch);
  const routeParams: RouteParamsType | undefined = route?.params;
  const storeIndex = routeParams?.storeIndex || '';
  const catalogItemId = Number(routeParams?.catalogItemId) || 0;
  const orderItemId = Number(routeParams?.orderItemId) || 0;
  const isCustomItem = Boolean(!state?.item?.itemImageUrls);
  const isMenu = Boolean(!runState.data?.store.elasticSearchIndex);
  const pageLoading = loadingItem || runState.loading || loadingBuyer;
  const isUPCMode = props?.isUPCMode;
  const scrollRef = useRef<ScrollView>(null);
  const storeMapsString = useAppSelector(
    gState => gState.onboarding.constants?.B2B_STORE_MAPS,
  );
  const {button} =
    useGetLocale(Modules.itemDetails, ShopperScreens.ItemDetails) ?? {};

  const muliFont = useFontTheme('muli');
  const theme = useColorThemeType();
  const shadow = useShadow('2');

  const marginValueAnimation = useRef(new Animated.Value(0)).current;
  const quantity =
    state.item?.totalupdatedQuantityByShopper ?? state.item?.quantity;
  const storeMasterId = runState?.data?.store?.storeMasterId;
  const storeId = routeParams?.storeId ?? runState?.data?.store?.id ?? 0;
  const isMeijerStore = useIsMeijerStore(storeId);

  const isAdjacentOOSItem = state.buyersForItem?.[0]?.isAdjacentOOSItem;
  const conversationId = state.buyersForItem?.[0]?.conversationId;

  const isCatalogItem = state.item?.id !== orderItemId && !isAdjacentOOSItem;
  const hasScannerEnabled = useMemo(() => {
    return isCatalogItem && !isWeb();
  }, [isCatalogItem]);

  const isMultipleBuyers = state?.buyersForItem?.length > 1;

  const storeMap = useMemo(() => {
    const storeMaps = storeMapsString ? JSON.parse(storeMapsString) : {};
    return storeMaps[storeId];
  }, [storeId, storeMapsString]);

  const imageData = useMemo(() => {
    if (isCustomItem)
      return [state?.item?.itemImageUrl || getDefaultImageUrl()];
    return state?.item?.itemImageUrls?.largeImages;
  }, [
    isCustomItem,
    state?.item?.itemImageUrl,
    state?.item?.itemImageUrls?.largeImages,
  ]);

  useEffect(() => {
    if (orderItemId && routeParams?.runId) {
      getCatalogItemForShopper(
        orderItemId,
        isUPCMode ?? false,
        routeParams?.runId,
      );
      if (!props?.isUPCMode)
        getItemLocationHistory(orderItemId, storeIndex, (response: any) => {
          if (dispatch)
            dispatch(ItemDetailsAction.setItemLocationData(response));
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeIndex, catalogItemId, orderItemId]);

  useEffect(() => {
    if (orderItemId && !isUPCMode && routeParams?.runId)
      getBuyersForItem(routeParams?.runId, orderItemId, catalogItemId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderItemId]);

  useEffect(() => {
    props.setLoading(pageLoading);
  }, [pageLoading, props]);

  useEffect(() => {
    if (routeParams?.runId && !isUPCMode)
      getRunForItemDetail(routeParams?.runId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeParams?.runId]);

  const getLoadingState = useMemo(
    () => pageLoading || !state?.item,
    [pageLoading, state?.item],
  );

  const marginValue = marginValueAnimation.interpolate({
    inputRange: [0, 60],
    outputRange: [-100, 0],
    extrapolate: 'clamp',
  });

  const {markItem} = useLocationUpdate(dispatch);
  const {getItemLocationHistory} = useUPCOrder();

  const refetchItemDetails = useCallback(() => {
    getItemLocationHistory(
      orderItemId,
      runState?.data?.store?.elasticSearchIndex ?? '',
      (response: any) => {
        if (dispatch) dispatch(ItemDetailsAction.setItemLocationData(response));
      },
    );
  }, [
    dispatch,
    getItemLocationHistory,
    orderItemId,
    runState?.data?.store?.elasticSearchIndex,
  ]);

  const btnComponent = useMemo(() => {
    return (
      <BottomButton
        disabled={
          props.bottomButtonLoading ||
          (!quantity && !isUPCMode) ||
          isAdjacentOOSItem
        }
        showDividers={false}
        showAlcoholicItemDisclaimer={!isCatalogItem && !isAdjacentOOSItem}
        onPress={() => props.onButtonPress(storeMasterId)}
        isLoading={props.bottomButtonLoading}
        loadingIndicator
        loadingText={button?.loading}
        title={props.buttonText}
        style={styles.buttonStyle}
        containerStyle={{backgroundColor: theme?.background.gray[200]}}
        leftComponent={
          <If condition={hasScannerEnabled}>
            <QRCode viewBox="-5 -2 22 17" style={styles.iconBox} />
          </If>
        }
      />
    );
  }, [
    button?.loading,
    hasScannerEnabled,
    isAdjacentOOSItem,
    isCatalogItem,
    isUPCMode,
    props,
    quantity,
    storeMasterId,
    theme?.background.gray,
  ]);

  const onScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const offsetY = e.nativeEvent.contentOffset.y;
      marginValueAnimation.setValue(offsetY > 60 ? 60 : offsetY);
    },
    [marginValueAnimation],
  );

  return (
    <React.Fragment>
      <ScrollView
        onScroll={onScroll}
        ref={scrollRef}
        scrollEventThrottle={10}
        contentContainerStyle={
          getLoadingState
            ? {
                ...styles.contentContainer,
                backgroundColor: theme?.background.white[100],
              }
            : undefined
        }>
        <React.Fragment>
          <If condition={getLoadingState || props.loading}>
            <LoaderComponents showBottomText={false} />
          </If>
          <If condition={!getLoadingState}>
            <Box
              style={[
                styles.bodyContainer,
                styles.subBodyContainer,
                {
                  backgroundColor: theme?.background?.white?.[100],
                },
                shadow,
              ]}>
              <If condition={state.item?.itemQuantity}>
                <Box style={[styles.pillBody]}>
                  <ItemQuantityPill
                    quantity={state.item?.itemQuantity as string}
                  />
                </Box>
              </If>
              <Carousal
                containerStyle={styles.itemContent}
                imageContainerStyle={styles.itemImage}
                showImageCaption={!isAdjacentOOSItem}
                data={imageData}
                showPagination={true}
              />
              <ItemInfo
                isUPCMode={isUPCMode ?? false}
                item={state?.item}
                upcItemId={orderItemId}
                orderItemId={orderItemId}
                esIndex={runState?.data?.store?.elasticSearchIndex ?? ''}
                isItemDetailPage={true}
                isB2BStore={isMeijerStore}
                storeMap={storeMap}
                storeId={storeId}
              />
              <If condition={state?.buyersForItem?.length}>
                <H6Text
                  style={[
                    styles.textStyle,
                    {
                      fontFamily: muliFont[600].normal,
                      color: theme?.text.blue[300],
                    },
                  ]}>
                  Total customers who purchased this item:{' '}
                  {state?.buyersForItem?.length}
                </H6Text>
                <If condition={state.item?.crossStoreOptions}>
                  <Divider style={styles.adjOOSDivider} />
                  <AdjacentOOSBanner />
                </If>
              </If>
            </Box>
            <If
              condition={
                state?.itemLocationInformation &&
                !props.isUPCMode &&
                !state?.item?.itemLocation
              }>
              <Box
                style={[
                  styles?.bodyContainer,
                  shadow,
                  {
                    backgroundColor: theme?.background?.white?.[100],
                  },
                ]}>
                <Box>
                  <If condition={loadingItem}>
                    <ActivityIndicator />
                  </If>
                  <If condition={!loadingItem}>
                    <ItemLocationInfo
                      locationData={
                        state?.itemLocationInformation as ItemLocationInformation
                      }
                      orderItemId={orderItemId}
                      showDivider={false}
                      dispatch={dispatch}
                      markItem={markItem}
                      action={ItemDetailsAction}
                      callback={refetchItemDetails}
                      onUpdate={true}
                    />
                  </If>
                </Box>
              </Box>
            </If>
            <If condition={isAdjacentOOSItem}>
              <AdjacentOOSChat
                conversationId={conversationId}
                runId={runState?.data?.id}
              />
            </If>
            <Box style={styles.childContainer}>
              <If condition={!isUPCMode}>
                <Box
                  pointerEvents={isAdjacentOOSItem ? 'none' : undefined}
                  style={[
                    styles.buyerDetailContainer,
                    // eslint-disable-next-line react-native/no-inline-styles
                    {
                      opacity: isAdjacentOOSItem ? 0.5 : undefined,
                      backgroundColor: theme?.background.white[100],
                      marginBottom: isMeijerStore ? 20 : 0,
                    },
                    shadow,
                  ]}>
                  <BuyerDetails
                    isMenu={isMenu}
                    isCustomItem={isCustomItem}
                    loadingBuyer={loadingBuyer}
                  />
                </Box>
              </If>
              <If condition={!isMeijerStore}>
                <ChangeCategory
                  category={state.item?.category}
                  catalogItemId={state.item?.id}
                  storeCategories={state.storeCategories}
                  storeIndex={routeParams?.storeIndex}
                  isAdjacentOOSItem={isAdjacentOOSItem}
                  isCustomItem={isCustomItem}
                />
              </If>
            </Box>
          </If>
        </React.Fragment>
      </ScrollView>
      <If condition={isMultipleBuyers && !props.bottomButtonLoading}>
        <Animated.View
          style={{
            marginBottom: marginValue,
          }}>
          {btnComponent}
        </Animated.View>
      </If>
      <If condition={!isMultipleBuyers && !props.bottomButtonLoading}>
        {btnComponent}
      </If>
    </React.Fragment>
  );
};

export default React.memo(ItemDetailsBody);
